import { EXTRACT_HEDGE_MS, READ_DEADLINE_MS } from '../src/resolve/limits';
import type { Candidate } from '../src/resolve/types';

export { READ_DEADLINE_MS };

/** Jev's screen runs before extraction, typically under a second, so the second model has usually started by then. */
export const READ_SLOW_MS = EXTRACT_HEDGE_MS + 1_000;

/** What the loader says under "Reading your stack" after `elapsedMs`. */
export function readHint(elapsedMs: number): string {
  return elapsedMs < READ_SLOW_MS ? 'This usually takes a few seconds.' : 'Taking longer than usual. When the first AI model is slow, Vulnder asks a second one.';
}

export interface AppConfig {
  displayName: string;
  baseUrl: string;
  turnstileSiteKey: string;
}

export interface Chip {
  input: string;
  status: 'resolved' | 'unrecognised';
  /** Exact items, or every close match for a vague name. Close items start with '?'; for an enterprise stack, each ends with ';team'. */
  items: { item: string; label: string; close: boolean; known: boolean; team?: string }[];
}

export interface ResolveResponse {
  source: 'manifest' | 'model';
  format?: string;
  chips: Chip[];
  droppedTransitive: number;
  /** What kind of stack this looks like; only used to order close matches. */
  profile: {
    scale: { value: 'enterprise' | 'smb' | 'home' | null; confidence: number };
    hosting: { value: 'cloud' | 'on_prem' | null; confidence: number };
  };
}

export interface Result {
  id: string;
  title: string | null;
  summary: string | null;
  publishedAt: string | null;
  tier: 'exploited' | 'likely' | 'backlog';
  evidence: {
    kevAddedAt: string | null;
    kevDueDate: string | null;
    knownRansomware: boolean;
    epss: number | null;
    epssPercentile: number | null;
    epssDate: string | null;
    /** NIST LEV: lower-bound chance it has already been exploited. */
    lev: number | null;
  };
  /** 'listed': the stack lists the CVE itself (a `cve:` item), so no version was checked. */
  confidence: 'version_confirmed' | 'product_match' | 'listed';
  match: 'exact' | 'close';
  matched: string[];
  fixedVersions: string[];
  cvss: { score: number; vector: string | null } | null;
  /** `exploit`: public attack code, when there is some; absent from feeds cached before it existed. */
  links: { advisory: string | null; patch: string | null; exploit?: string | null; reports?: string[] };
  /** Trusted security teams' write-ups of it, earliest first (src/match/match.ts); absent from older feeds. */
  reports?: Report[];
  priority: Priority;
  /** 0–100: orders results within a priority. */
  score: number;
  /** Why it got this priority, most important first. */
  reasons: string[];
  /** Its family (similar CVEs in the same product), when this feed has other members or one is exploited. */
  family: string | null;
  /** Other results in this feed from the same family. */
  related: string[];
  why: {
    /** The rule that decided the band; null for Track. */
    decisive: Reason | null;
    others: Reason[];
    /** Signals the band couldn't use. */
    missing: string[];
  };
  /** Suggested time to fix or mitigate, in hours (guidance); null for Track. */
  respondWithinHours: number | null;
  /** For urgent CVEs with no fixed version known: what to do meanwhile. */
  mitigation: { action: string | null; advisory: string | null } | null;
  /** Older than the chosen window, shown because it's exploited, likely to be, or CVSS 9.9+ in the last year (or because it's listed). */
  beforeWindow?: boolean;
  /** Listed CVEs rejected as duplicates of this one. */
  replaces?: string[];
}

export interface Reason {
  text: string;
  kind: 'evidence' | 'prediction' | 'severity' | 'context';
}

export type Priority = 'act' | 'attend' | 'watch' | 'track';

export interface FixItem {
  /** Stack item; a leading '?' marks a close match. */
  item: string;
  score: number;
  counts: Record<Priority, number>;
  vulns: string[];
  fixable: number;
  /** Set when it's out of support, losing it soon, or kept going by paid extended support. */
  support?: SupportState;
  /** Groups listed CVEs (by their product, or one CVE alone) rather than naming a stack item. */
  listed?: boolean;
  /** The product's display name, for a listed group. */
  label?: string;
}

export type SupportState = 'eol' | 'ending' | 'covered';

/** A stack item's vendor support finding (src/match/support.ts). */
/** A trusted security team's write-up of a CVE: never evidence of exploitation. */
export interface Report {
  source: string;
  /** The post's title as plain text; shown as text, never HTML. */
  title: string | null;
  url: string;
  /** YYYY-MM-DD */
  publishedAt: string;
  /** The post calls the bug critical or urgent. */
  urgent: boolean;
}

/** Stack items whose CVEs CISA added to KEV in the last 90 days, whatever version they affect (src/match/targeted.ts). */
export interface TargetedNotice {
  /** The stack items it's about. */
  items: string[];
  /** Newest first; addedAt is YYYY-MM-DD. */
  kev: { id: string; addedAt: string }[];
  /** The product of listed CVEs (a Fix first group), not an item the stack names. */
  listed?: true;
  /** That product's display name, when known. */
  label?: string;
}

export interface SupportNotice {
  state: SupportState;
  /** "Windows Server 2012 R2". */
  name: string;
  /** When support ended or ends (YYYY-MM-DD); null when no date is known. */
  date: string | null;
  /** When paid extended support it could have ends; null when there is none or the user has it. */
  esuUntil: string | null;
  /** The dates counted the user's ESU. */
  esu: boolean;
  edge: boolean;
  source: 'endoflife' | 'cve';
  cve: string | null;
  /** The stack items it's about. */
  items: string[];
}

export interface Change {
  vulnId: string;
  type: 'published' | 'kev_added' | 'exploitation_reported' | 'epss_crossed' | 'fix_released';
  occurredAt: string;
  detail: Record<string, unknown>;
  title: string | null;
  tier?: Result['tier'];
}

export interface Feed {
  stack: string;
  days: number;
  generatedAt: string;
  links: { page: string; json: string; atom: string; badge: string };
  versionCheckUnavailable: boolean;
  summary: Record<Result['tier'], number>;
  priorities: Record<Priority, number>;
  /** Matched stack items in the order to fix them. */
  fixFirst: FixItem[];
  /** Out of support, losing it soon, or on paid extended support; absent from feeds cached before it existed. */
  support?: SupportNotice[];
  /** Items with CVEs CISA added to KEV in the last 90 days; absent from feeds cached before it existed. */
  targeted?: TargetedNotice[];
  changes: Change[];
  results: Result[];
  watching: string[];
  /** For a stack with `cve:` items: listed IDs that were rejected or aren't published CVEs. */
  listed?: {
    rejected: { id: string; replacedBy: string[] }[];
    notFound: string[];
  };
}

export interface Health {
  sources: Record<string, { health: 'ok' | 'stale' | 'error' | 'never'; lastSuccessAt: string | null }>;
  /** Successful /api/feed replies so far, up to 15 minutes behind. */
  checksServed?: number;
}

/** This browser's allowance of different stacks an hour (src/lib/pass.ts). */
export interface PassStatus {
  active: boolean;
  used: number;
  limit: number;
  resetsAt: string | null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fallback?: string,
    readonly reason?: string,
  ) {
    super(message);
  }
}

async function json<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as { error?: string; fallback?: string; reason?: string };
  if (!res.ok) throw new ApiError(body.error ?? `request failed (${res.status})`, res.status, body.fallback, body.reason);
  return body as T;
}

/**
 * How long the browser waits before saying a request is stuck, rather than
 * loading forever. Reading a description waits longest: the server gives up
 * on its own model calls within READ_DEADLINE_MS, so its manual fallback
 * answers first, with a few seconds spare for D1 and the network. A feed is
 * cached or takes a few seconds; OSV gives up after 5.
 */
export const RESOLVE_TIMEOUT_MS = READ_DEADLINE_MS + 4_000;
export const FEED_TIMEOUT_MS = 15_000;
export const SMALL_TIMEOUT_MS = 10_000;

/** fetch with a deadline. A timeout or a dropped connection becomes an ApiError that says which. */
export async function request(url: string, init: RequestInit, timeoutMs: number, fetchImpl: typeof fetch = (...a) => fetch(...a)): Promise<Response> {
  try {
    return await fetchImpl(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
  } catch (err) {
    if ((err as { name?: unknown } | null)?.name === 'TimeoutError') {
      throw new ApiError('Vulnder took too long to answer. Try again in a moment.', 0, undefined, 'timeout');
    }
    throw new ApiError("Couldn't reach Vulnder. Check your connection and try again.", 0, undefined, 'network');
  }
}

/**
 * A lock for sends: `run` does nothing, and says so, while an earlier send is
 * still out, so a double click or a second drop sends one request. Shared by
 * both tabs, since either one's lookup spends the same allowance.
 */
export function oneAtATime(): (send: () => Promise<void>) => Promise<boolean> {
  let busy = false;
  return async (send) => {
    if (busy) return false;
    busy = true;
    try {
      await send();
    } finally {
      busy = false;
    }
    return true;
  };
}

export const getPass = () => request('/api/pass', {}, SMALL_TIMEOUT_MS).then((r) => json<PassStatus>(r));
/** Trades a Turnstile token for a pass, as a description's lookup does; the CVE list tab's lookup goes straight to the feed. */
export const earnPass = (turnstileToken: string) =>
  request('/api/pass', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ turnstileToken }) }, SMALL_TIMEOUT_MS).then((r) =>
    json<PassStatus>(r),
  );

export const getConfig = () => request('/api/config', {}, SMALL_TIMEOUT_MS).then((r) => json<AppConfig>(r));
export const getHealth = () => request('/api/health', {}, SMALL_TIMEOUT_MS).then((r) => json<Health>(r));
export const getFeed = (s: string, days: number) =>
  request(`/api/feed?s=${encodeURIComponent(s)}${days === 30 ? '' : `&days=${days}`}`, {}, FEED_TIMEOUT_MS).then((r) => json<Feed>(r));

export function resolve(body: { text: string } | { candidates: Candidate[] }, turnstileToken: string): Promise<ResolveResponse> {
  return request(
    '/api/resolve',
    { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ...body, turnstileToken }) },
    RESOLVE_TIMEOUT_MS,
  ).then((r) => json<ResolveResponse>(r));
}
